import { Button } from '@ipfabric/ui-components/Button/Button';
import { TextAreaInput } from '@ipfabric/ui-components/TextAreaInput/TextAreaInput';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Security } from '@ipfabric/utils';
import { SecuritySchema } from 'components/SecuritySchema/SecuritySchema';
import type { SecuritySchemaDataSource } from 'components/SecuritySchema/model/SecuritySchemaDataSource';
import type { RuleChainScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import { useMemo, useState } from 'react';
import {
  type ErrorInfo,
  type FC,
  type FormEvent,
  PureComponent,
  type ReactNode,
} from 'react';

const initialDeviceSecurity: Security.DeviceSecurity = {
  machineAcl: {
    entryChainName: 'demoChain',
    namedTests: {
      anybody: {
        operator: 'true',
        original: 'Allow anybody',
      },
    },
    ruleChains: {
      demoChain: {
        defaultAction: {
          type: 'deny',
        },
        name: 'demoChain',
        rules: [
          {
            name: 'Open Day',
            active: true,
            action: {
              type: 'allow',
              original: 'permit',
            },
            test: {
              reference: {
                dictionary: 'local',
                key: 'anybody',
              },
            },
          },
        ],
      },
    },
  },
  machineNat44: null,
  machinePbr: null,
  machineZones: null,
  machineCloud: null,
};

const entryPoint: RuleChainScope = {
  name: 'demoChain',
  securityType: 'acl',
  type: 'ruleChain',
};

class SandboxErrorBoundary extends PureComponent<{
  onCatch: () => void;
  children: ReactNode;
}> {
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // biome-ignore lint/suspicious/noConsole: put errors from the sandbox just to console, easy way for dev tool without crashing the app
    console.error(error, errorInfo);
    this.props.onCatch();
  }

  static getDerivedStateFromError() {
    return {};
  }

  render() {
    return this.props.children;
  }
}
export const SecuritySchemaSandboxGallerySection: FC = () => {
  const initialCode = useMemo(
    () => JSON.stringify(initialDeviceSecurity, undefined, 2),
    [],
  );
  const [draftCode, setDraftCode] = useState(initialCode);
  const [code, setCode] = useState<string | null>(initialCode); // null === error
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setCode(draftCode);
  };

  const handleCatch = useStableCallback(() => {
    setCode(null);
  });

  const dataSource: SecuritySchemaDataSource | null = useMemo(() => {
    try {
      return code === null
        ? null
        : {
            data: {
              hostname: 'TEST HOST',
              security: JSON.parse(code),
            },
          };
    } catch (e) {
      // biome-ignore lint/suspicious/noConsole: put errors from the sandbox just to console, easy way for dev tool without crashing the app
      console.error('JSON parse error', e);
      return null;
    }
  }, [code]);

  return (
    <div className='flex flex-1 flex-row items-stretch'>
      <form
        className='flex flex-1 flex-col items-stretch'
        onSubmit={handleSubmit}
      >
        <p>
          Note: this sandbox is an experimental feature meant to be used purely
          for internal development purposes.
        </p>
        <p>
          Since joi schemas for DeviceSecurity aren&apos;t available in
          frontend, we attempt to render any input and try to catch an error
          more or less gracefully.
        </p>
        <p>
          <Button type='submit' variant='primary'>
            Evaluate
          </Button>
        </p>
        <p>
          <strong>Enter valid DeviceSecurity object in JSON below:</strong>
        </p>
        <TextAreaInput
          className='flex-1'
          value={draftCode}
          onChange={(e) => setDraftCode(e.target.value)}
        />
      </form>
      <SandboxErrorBoundary onCatch={handleCatch}>
        {dataSource === null ? (
          <div className='flex-1'>
            <p className='text-danger'>
              Invalid input. See browser console for error clues.
            </p>
          </div>
        ) : (
          <SecuritySchema
            className='flex-1'
            entryPoint={entryPoint}
            dataSource={dataSource}
          />
        )}
      </SandboxErrorBoundary>
    </div>
  );
};
